import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { navSites } from "@/mocks/navSites";

const hotKeywords = ["视频", "购物", "地图", "开源", "外卖", "学习"];

interface SearchFormProps {
  onSearch?: (keyword: string) => void;
  initialKeyword?: string;
  variant?: "hero" | "inline";
}

export default function SearchForm({
  onSearch,
  initialKeyword = "",
  variant = "hero",
}: SearchFormProps) {
  const navigate = useNavigate();
  const [value, setValue] = useState(initialKeyword);
  const [focused, setFocused] = useState(false);

  const suggestions = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return [];
    return navSites
      .filter(
        (site) =>
          site.name.toLowerCase().includes(q) ||
          site.host.toLowerCase().includes(q) ||
          site.tags.some((tag) => tag.toLowerCase().includes(q)),
      )
      .slice(0, 5);
  }, [value]);

  const runSearch = (raw: string) => {
    const q = raw.trim();
    if (!q) return;
    setFocused(false);
    if (onSearch) {
      onSearch(q);
    } else {
      navigate(`/search?q=${encodeURIComponent(q)}`);
    }
  };

  const isHero = variant === "hero";

  return (
    <div className="w-full">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          runSearch(value);
        }}
        className={`relative flex items-center bg-background-50 rounded-lg ${
          isHero ? "h-12 md:h-14" : "h-11"
        } ${isHero ? "ring-4 ring-background-50/25" : "border border-background-200"}`}
      >
        <span className="absolute left-4 w-5 h-5 flex items-center justify-center text-foreground-400">
          <i className={`ri-search-line ${isHero ? "text-lg" : "text-base"}`} />
        </span>
        <input
          type="text"
          value={value}
          onFocus={() => setFocused(true)}
          onBlur={() => window.setTimeout(() => setFocused(false), 160)}
          onChange={(event) => setValue(event.target.value)}
          placeholder="输入网站名称、域名或关键词，例如：视频、外卖、GitHub"
          className="flex-1 h-full pl-11 pr-2 bg-transparent text-sm md:text-base text-foreground-900 placeholder:text-foreground-400 focus:outline-none rounded-l-lg"
        />
        <button
          type="submit"
          className={`h-full px-5 md:px-7 rounded-r-lg bg-accent-500 text-background-50 text-sm md:text-base font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap`}
        >
          搜索
        </button>

        {focused && suggestions.length > 0 && (
          <div className="absolute left-0 right-0 top-full mt-2 z-40 bg-background-50 rounded-lg border border-background-200 overflow-hidden animate-pop-in">
            {suggestions.map((site) => (
              <Link
                key={site.id}
                to={`/detail/${site.id}`}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 cursor-pointer"
              >
                <span className="w-7 h-7 rounded-md bg-primary-100 text-primary-600 text-xs font-bold flex items-center justify-center">
                  {site.letter}
                </span>
                <span className="text-sm text-foreground-900">{site.name}</span>
                <span className="text-xs text-foreground-400 ml-auto">{site.host}</span>
              </Link>
            ))}
            <button
              type="button"
              onMouseDown={() => runSearch(value)}
              className="w-full text-left px-4 py-2.5 text-xs text-primary-600 bg-background-100 hover:bg-primary-50 cursor-pointer"
            >
              查看全部搜索结果
            </button>
          </div>
        )}
      </form>

      {isHero && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs text-background-50/80">热门搜索：</span>
          {hotKeywords.map((word) => (
            <button
              key={word}
              type="button"
              onClick={() => runSearch(word)}
              className="px-3 py-1 rounded-full bg-background-50/15 text-background-50 text-xs hover:bg-background-50/30 transition-colors cursor-pointer whitespace-nowrap"
            >
              {word}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}